import * as _ from "lodash"

import {
    ISearch,
    ISearchProfile,
    ISearchProvider,
    HorizAlignment,
    SearchProviderFieldType,
    ISearchProviderField,
    ISearchRequestSubset,
    IProfileFilter,
    ISearchSort
} from "../PA.UIM.Core/PA.UIM.Core.IF.Search"
import {SearchUtils} from "../PA.UIM.Core/SearchUtils";
import {PASearch} from "../PA.Core/PASearch";
import {WB_PeriodTypeRow} from "../PA.Core/PAdbSchemaRows";


const vWB_Prod_Fields: { [id: string]: ISearchProviderField } = {
    WB_PERIOD_ID: {
        type: SearchProviderFieldType.number,
        renderOptions: {
            columnName: 'Time WB Id',
            columnResourceName: 'colPeriodWBId',
            alignment: HorizAlignment.Center
        },
        domainOptions: {}
    },
    WB_MDSET_ID: {
        type: SearchProviderFieldType.number,
        renderOptions: {
            columnName: 'Master Data Set WB Id',
            columnResourceName: 'colMDSetId',
            alignment: HorizAlignment.Center
        },
        domainOptions: {}
    },
    WB_PERIODM_ID: {
        type: SearchProviderFieldType.number,
        renderOptions: {
            columnName: 'Time Type WB Id',
            columnResourceName: 'colPeriodMId',
            alignment: HorizAlignment.Center
        },
        domainOptions: {}
    },
    PERIOD_ID: {
        type: SearchProviderFieldType.string,
        renderOptions: {columnName: 'ID', columnResourceName: 'colProdId', alignment: HorizAlignment.Left},
        domainOptions: {maxLength: 50}
    },
    PERIOD_NAME: {
        type: SearchProviderFieldType.string,
        renderOptions: {columnName: 'Name', columnResourceName: 'colProdName', alignment: HorizAlignment.Left},
        domainOptions: {maxLength: 256}
    },
    PERIOD_TYPE_ID: {
        type: SearchProviderFieldType.string,
        renderOptions: {columnName: 'Type Id', columnResourceName: 'colLevel', alignment: HorizAlignment.Left},
        domainOptions: {maxLength: 50}
    },
    PERIOD_TYPE_NAME: {
        type: SearchProviderFieldType.string,
        renderOptions: {columnName: 'Type Name', columnResourceName: 'colLevel', alignment: HorizAlignment.Left},
        domainOptions: {maxLength: 256}
    },
    SOURCE_ID: {
        type: SearchProviderFieldType.string,
        renderOptions: {columnName: 'Source', columnResourceName: 'colSourceId', alignment: HorizAlignment.Left},
        domainOptions: {maxLength: 50}
    },
    PERIOD_START_DT: {
        type: SearchProviderFieldType.sapdate,
        renderOptions: {
            columnName: 'Start Date',
            columnResourceName: 'colStart',
            alignment: HorizAlignment.Center,
            dateFormat: "MM-dd-yy"
        },
        domainOptions: {}
    },
    PERIOD_END_DT: {
        type: SearchProviderFieldType.sapdate,
        renderOptions: {
            columnName: 'End Date',
            columnResourceName: 'colEnd',
            alignment: HorizAlignment.Center,
            dateFormat: "MM-dd-yy"
        },
        domainOptions: {}
    }
}

const vWB_Time_KeyFields =
    {
        id: 'WB_PERIOD_ID',
        id_ui: 'PERIOD_ID',
        name: 'PERIOD_NAME'
    }

class TimeManagerSearchProvider
    implements ISearchProvider {

    _WB_MDSET_ID: number
    _PERIOD_TYPE: string
    _Profile: ISearchProfile

    constructor(wbMDSetId: number, productType: string, profile: ISearchProfile) {
        this._WB_MDSET_ID = wbMDSetId
        this._PERIOD_TYPE = productType
        this._Profile = profile
    }

    get keyFields() {
        return vWB_Time_KeyFields
    }

    get searchProviderFields() {
        return vWB_Prod_Fields
    }


    search(requestSpec: ISearchRequestSubset): Promise<any[]> {
        return new Promise<any[]>((resolve, error) => {
            requestSpec.where.WB_MDSET_ID = this._WB_MDSET_ID
            requestSpec.where.PERIOD_TYPE_ID = this._PERIOD_TYPE
            // @ts-ignore
            requestSpec.where.WB_PERIODM_ID = this._Profile.periodTypeRow?.cif.WB_PERIODM_ID


            let findRequest = SearchUtils.makeSearchRequest(requestSpec,
                'vUI_Time_Manager_Search',
                this.searchProviderFields,
                [])

            PASearch.findByRequest(findRequest)
                .then((res: any[]) => {
                    let joinColumns = _.filter(this._Profile.profileDisplayFields, aField => _.isObject(aField));
                    let joinFields = _.flatMap(joinColumns, 'fields');
                    let singleFields = _.filter(this._Profile.profileDisplayFields, aField => !_.isObject(aField));
                    let displayFields = _.uniq(_.concat(joinFields, singleFields, Object.values(this.keyFields)));

                    function unique(arr: any[], keyProps: any[]) {
                        const kvArray: any[][] = arr.map(entry => {
                            const key = keyProps.map(k => entry[k]).join('|');
                            return [key, entry];
                        });
                        // @ts-ignore
                        const map = new Map(kvArray);
                        return Array.from(map.values());
                    }

                    resolve(unique(res, displayFields))
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    distinctValues(fieldName: string): Promise<string[] | number[]> {
        return new Promise<string[] | number[]>((resolve, error) => {
            let findRequest = SearchUtils.makeSearchRequest({
                    where: {
                        WB_MDSET_ID: this._WB_MDSET_ID,
                        PERIOD_TYPE_ID: this._PERIOD_TYPE
                    }
                },
                'vUI_Time_Manager_Search',
                this.searchProviderFields,
                [fieldName])


            PASearch.findByRequest(findRequest)
                .then((res: any[]) => {
                    resolve(_.map(res, (aRow) => {
                        return aRow[fieldName]
                    }))
                })
                .catch((err) => {
                    error(err)
                })
        })
    }
}

class TimeManagerSearchProfile
    implements ISearchProfile {

    constructor(wbMDSetId: number, periodTypeId: string, periodTypeRow: WB_PeriodTypeRow) {
        this.wbMasterDataSetId = wbMDSetId
        this.TimeTypeId = periodTypeId
        this.TimeTypeRow = periodTypeRow

        this.aSearchProvider = new TimeManagerSearchProvider(wbMDSetId, periodTypeId, this)
    }

    wbMasterDataSetId: number
    TimeTypeId: string
    TimeTypeRow: WB_PeriodTypeRow

    aSearchProvider: ISearchProvider = undefined

    profileId = "default"
    profileName = "Default Profile"
    renderOptions = {ui5ResourceId: "TimeManagerSearchProfile"}

    get productTypeRow(): WB_PeriodTypeRow {
        return this.TimeTypeRow
    }

    get profileFilter(): IProfileFilter[] {

        let coreFilters = [
            {profileFilterName: 'ID', searchProviderFieldId: 'PERIOD_ID', renderOptions: {}},
            {profileFilterName: 'Name', searchProviderFieldId: 'PERIOD_NAME', renderOptions: {}}
        ]


        return coreFilters
    }

    get profileDisplayFields(): string[] {

        let coreDisplayFields = [
            "PERIOD_ID",
            "PERIOD_NAME",
            "PERIOD_START_DT",
            "PERIOD_END_DT",
            "CNT"
        ]

        return coreDisplayFields

    }

    profileHyperlinkFields = ['PERIOD_ID']
    profileSortIdDefault = ""
    profileSorts: ISearchSort[] = []


    get profileSearchProvider() {
        return this.aSearchProvider
    }
}


export class TimeManagerSearch
    implements ISearch {
    constructor(wbMDSetId: number,
                periodType: string,
                periodTypeRow: WB_PeriodTypeRow,
                options?: any) {

        this.searchProfiles = [new TimeManagerSearchProfile(wbMDSetId, periodType, periodTypeRow)]
    }

    searchName = "Select Time(s)"
    renderOptions = {ui5ResourceId: "TimeManagerSearch"}
    searchProfileIdDefault = "default"
    searchProfiles: ISearchProfile[] = []
    autoSearch = true

}
